import { createSelector } from "reselect";
import { timeParse, timeFormat } from "d3-time-format"
import { timeYear } from "d3-time"

const tickerPair = ([before, after], key) => {
  const aligned = [];
  let match;
  for (let i = 0; i < Math.max(before.length, after.length); i++) {
    if (before[i]) {
      match = after.filter(o => o && o[key] === before[i][key])[0];
      aligned.push({
        key: `before(${before[i] && before[i][key]})` +
          `after(${match && match[key]})`,
        before: before[i],
        after: match,
        sorter: 1 * +!!before + -1 * +!!match
      });
    } else {
      match = before.filter(o => o && o[key] === after[i][key])[0];
      aligned.push({
        key: `before(${match && match[key]})` +
          `after(${after[i] && after[i][key]})`,
        before: match,
        after: after[i],
        sorter: -1 * +!!match + 1 * +!!after
      });
    }
  }
  return [
    ...aligned.filter(o => o.sorter === -1),
    ...aligned.filter(o => o.sorter === 0),
    ...aligned.filter(o => o.sorter === 1)
  ];
};

const pair = ([unsortedBefore, unsortedAfter], key) => {
  const aligned = [];
  const sortFunc = (a, b) => (a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0);
  const before = [...unsortedBefore].sort(sortFunc);
  const after = [...unsortedAfter].sort(sortFunc);
  let i = 0, j = 0;
  while (i < before.length || j < after.length) {
    if (before[i] && after[j] && before[i][key] > after[j][key]) {
      aligned.push({
        key: `before(undefined)after(${after[j][key]})`,
        before: undefined,
        after: after[j],
        sorter: 1
      });
      j += 1;
    } else if (before[i] && after[j] && before[i][key] < after[j][key]) {
      aligned.push({
        key: `before(${before[i][key]})after(undefined)`,
        before: before[i],
        after: undefined,
        sorter: 1
      });
      i += 1;
    } else {
      aligned.push({
        key: `before(${before[i] && before[i][key]})` +
          `after(${after[j] && after[j][key]})`,
        before: before[i],
        after: after[j],
        sorter: before[i] && after[j] && before[i][key] === after[j][key]
          ? -1
          : 0
      });
      i += 1;
      j += 1;
    }
  }
  return [...aligned].sort((a, b) => a.sorter - b.sorter);
};

const computeExposures = ({ keys, values }, groupBy, valueMap) =>
  keys.map((key, i) => ({
    value: valueMap ? valueMap(key) : key,
    weighting: values[i],
    ...(groupBy ? { group: groupBy(key) } : {})
  }));

const computeAllExposures = d => ({
  assetClass: computeExposures(d.assetClassExposures[0]),
  marketCap: computeExposures(d.marketCapExposures[0], null, cap =>
    cap.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()
  ),
  sector: computeExposures(d.sectorExposures[0], null, sec =>
    sec.replace(/&amp;/g, "and")
  ),
  country: computeExposures(
    d.countryExposures[0],
    value => (value === "United States" ? value : "International")
  ),

  // Add the Fixed income exposures. If the portfolio or benchmark is missing then add a key of 'N/A' with 100% weight
  creditQuality: computeExposures(d.creditQualityExposures !== null && Array.isArray(d.creditQualityExposures) ?
    d.creditQualityExposures[0] :
    { date: d.assetClassExposures[0].date, keys: ['N/A'], values: [1] }),
  maturity: computeExposures(d.maturityExposures !== null && Array.isArray(d.maturityExposures) ? 
    d.maturityExposures[0] :
    { date: d.assetClassExposures[0].date, keys: ['N/A'], values: [1] }),
  // currency: computeExposures(d.currencyExposures[0]),
});

const ratesBpsFactorShift = (yrsToMaturity, rateAnchor, bpsShift) => {
  // see crunchBPSFactorShift.js for full logic
  if(rateAnchor*1e4 < -1*bpsShift){ return 0 }

  const assumedBaseValue = 1000
  const semiAnnualCoupon = .5*(assumedBaseValue*rateAnchor)
  const rateEnd = rateAnchor+(bpsShift/1e4)
  
  let NPVofCashFlowsAnchor = 0
  let NPVofCashFlowsEnd = 0
  for(let i = 1; i <= (2*yrsToMaturity); i++){
    NPVofCashFlowsAnchor += semiAnnualCoupon / Math.pow(1+(.5*rateAnchor),i)
    NPVofCashFlowsEnd    += semiAnnualCoupon / Math.pow(1+(.5*rateEnd),i)
  }
  return (NPVofCashFlowsEnd + (assumedBaseValue/Math.pow(1+rateEnd, yrsToMaturity))) / 
    (NPVofCashFlowsAnchor + (assumedBaseValue/Math.pow(1+rateAnchor, yrsToMaturity))) - 1
}

// weights, shifts, customStressTestColumns, riskFreeRates
// rates lets us look up current rate based on name
const computeCustomStressTest = (portfolio, factors, rates) =>
  Object.keys(factors)
    .filter(k => k !== "date")
    .reduce((t,k) => {
      let shift = factors[k]
      if(rates[k]){
        const { yrsToMaturity, rate } = rates[k]
        shift = ratesBpsFactorShift(yrsToMaturity, rate, shift)
      }
      return t + (!!portfolio[k] ? portfolio[k]*shift : 0)
    }, 0)

const calculateLevels = (levels, datefield, version, keys, name) => 
  levels[datefield].map((_, i) => {
    const ret = { type_tag: version, ...(name ? { name } : {}) };
    keys.forEach(key => {
      if (Array.isArray(levels[key])) {
        ret[key] = levels[key][i];
      }
    });
    return ret;
  })

const singularByPlural = {
  assetClasses: "assetClass",
  categories: "category",
  cusips: "cusip",
  entities: "entity",
  expenseRatios: "expenseRatio",
  expenseRatiosGross: "expenseRatioGross",
  expenseRatiosAdjusted: "expenseRatioAdjusted",
  expenseNotes: "expenseNote",
  famaFrench5Factors: "famaFrench5Factor",
  fundPageURLs: "fundPageURL",
  inceptionDates: "inceptionDate",
  stdPerformance: "stdPerformance",
  geoFocuses: "geoFocus",
  isins: "isin",
  names: "name",
  sedols: "sedol",
  tickers: "ticker",
  types: "type",
  weights: "weighting",
  wtRegions: "domicile",
  tickerWeights: "tickerWeighting",
};


const MORNINGSTAR_URL = 'https://doc.morningstar.com/docdetail.aspx?clientid=schwab&key=84b36f1bf3830e07&cusip=';

export const dataSelector = createSelector(
  [
    state => state.analysis, 
    state => state.performanceTableFields, 
    state => state.customStressTests,
    state => state.customStressTestColumns,
    state => state.riskFreeRates,
    state => Object.keys(state.stressTestKeys),
    state => state.complianceText,
  ],
  (data, performanceTableFields, customStressTests, customStressTestColumns, riskFreeRates, stressTestKeys, complianceText) => {
    if (data == null || typeof data !== "object") return data;
    
    const
      famaFrench5FactorsKeys = ["beta", "investments", "quality", "size", "value"],
      performanceTitles = { 
        Performance: complianceText.returnsPerformanceTableLabel, 
        Risk: complianceText.volatilityPerformanceTableLabel 
      };
    
    const [original, proposed] = data.map(portfolio => {
      const {
        holdings: [holdings],
        levels,
        aggregateDistributions,
        version,
        stressTests,
        benchmarkStressTests,
        famaFrench5Factors,
        fm16FactorModel,
        monteCarlos,
      } = portfolio;

      holdings.tickerWeights = [];
      const tickerIndices = [...holdings.tickers].reduce(
        (t, v, i) => ({ ...t, [v]: t[v] ? [...t[v], i] : [i] }),
        {}
      );
      for (let ticker in tickerIndices) {
        const len = tickerIndices[ticker].length;
        const orig = tickerIndices[ticker][len - 1];
        holdings.tickerWeights[orig] = holdings.weights[orig];
        if (len > 1) {
          const dupes = tickerIndices[ticker].slice(0, len - 1);
          for (var j = 0; j < dupes.length; j++) {
            holdings.tickerWeights[orig] += holdings.weights[dupes[j]];
            holdings.tickerWeights[dupes[j]] = 0;
          }
        }
      }

      const holdingsKeys = Object.keys(holdings);
      const levelsKeys = Object.keys(levels);
      const aggregateDistributionsKeys = Object.keys(aggregateDistributions);
      const performanceTableKeys = performanceTableFields.map(o => o.key);
      const monteCarlo = monteCarlos[monteCarlos.length - 1];
      const monteCarloKeys = Object.keys(monteCarlo);
      const benchmarks = portfolio.benchmarks || [];

      return {
        version,
        holdings: holdings.entities.map((_, i) => {
          const ret = { morningstar: MORNINGSTAR_URL+holdings.cusips[i] };
          holdingsKeys.forEach(key => {
            if (Array.isArray(holdings[key])) {
              ret[singularByPlural[key]] = key === "inceptionDates"
                ? timeFormat("%m/%d/%Y")(timeParse("%Y-%m-%dT%H:%M:%S")(holdings[key][i]))
                : holdings[key][i];
            }
          });
          for (var k in ret.famaFrench5Factor) {
            if (k !== "date")
              ret["fff-" + k] = ret.famaFrench5Factor[k] 
                ? ret.famaFrench5Factor[k].toFixed(3) 
                : null;
          }
          for (var j in ret.stdPerformance) {
            ret[j] = j === "asOfDate"
              ? ret.stdPerformance[j]
              : ret.stdPerformance[j] 
                ? (100*ret.stdPerformance[j]).toFixed(2)
                : ret.stdPerformance[j];
          }
          return ret;
        }),
        levels: calculateLevels(levels, "date", version, levelsKeys),
        benchmarksLevels: benchmarks.map(b => 
          calculateLevels(b.levels, "date", version, levelsKeys, b.name)),
        drawdownLevels: calculateLevels(levels, "dateDrawdowns", version, ["dateDrawdowns", "monthlyRollingDrawdowns"]),
        benchmarksDrawdownLevels: benchmarks.map(b =>
          calculateLevels(b.levels, "dateDrawdowns", version, ["dateDrawdowns", "monthlyRollingDrawdowns"], b.name)),
        aggregateDistributions: aggregateDistributions.calendarYear
          .map((_, i) => {
            const ret = { type_tag: version };
            aggregateDistributionsKeys.forEach(key => {
              if (Array.isArray(aggregateDistributions[key])) {
                ret[key] = aggregateDistributions[key][i];
              }
            });
            return ret;
          })
          .sort((a, b) => +a.calendarYear - +b.calendarYear),
        stressTests: stressTestKeys.reduce((o, k) => {
          o[k] = stressTests[k];
          return o;
        }, {}),
        benchmarkStressTests: benchmarkStressTests.reduce((t,v) => ({ 
          ...t,
          ...(v.stressTests
            ? {
                [v.benchmarkName]: stressTestKeys.reduce((o, k) => {
                  o[k] = v.stressTests[k];
                  return o;
                }, {})
              }
            : {})
        }), {}),
        benchmarkFm16FactorModel: benchmarkStressTests.reduce((t,v) => ({
          ...t,
          ...(v.fm16FactorModel && v.fm16FactorModel[0]
            ? { [v.benchmarkName]: v.fm16FactorModel[0] }
            : {})
        }), {}),
        famaFrench5Factors: famaFrench5FactorsKeys.reduce((o, k) => {
          if (k !== "date") o["fff-" + k] = famaFrench5Factors[0][k] 
            ? famaFrench5Factors[0][k].toFixed(3) 
            : null;
          return o;
        }, {}),
        exposures: computeAllExposures(portfolio),
        benchmarksExposures: !benchmarks 
          ? [] 
          : benchmarks.map(b => ({ ...computeAllExposures(b), name: b.name })),
        performance: Object.keys(performanceTitles).reduce((o, title) => {
          const raw = portfolio["std" + title][0];
          o[title] = {
            performanceTitle: performanceTitles[title],
            asOfDate: raw.asOfDate
          };
          performanceTableKeys.forEach(k => {
            o[title][k + "NAV"] = raw[k + "NAV"] 
              ? (100*raw[k + "NAV"]).toFixed(2)
              : raw[k + "NAV"];
            o[title][k + "MP"] = raw[k + "MP"]
              ? (100*raw[k + "MP"]).toFixed(2)
              : raw[k + "MP"];
          });
          return o;
        }, {}),
        benchmarksPerformance: benchmarks.map(b => 
          Object.keys(performanceTitles).reduce((o, title) => {
            const raw = b["std" + title][0];
            o[title] = {
              performanceTitle: performanceTitles[title],
              asOfDate: raw.asOfDate,
              name: b.name
            };
            performanceTableKeys.forEach(k => {
              o[title][k + "NAV"] = raw[k + "NAV"] 
                ? (100*raw[k + "NAV"]).toFixed(2)
                : raw[k + "NAV"];
              o[title][k + "MP"] = raw[k + "MP"]
                ? (100*raw[k + "MP"]).toFixed(2)
                : raw[k + "MP"];
            });
            return o;
          }, {})),
        date: holdings.date,
        fm16FactorModel: fm16FactorModel[0],
        customStressTestsData: customStressTests.reduce((t,v) => 
          [
            ...t, 
            holdings.fm16FactorModels.reduce((tt,vv,ii) => [
              ...tt,
              {
                key: holdings.tickers[ii],
                value: computeCustomStressTest(vv, v.factorShifts, riskFreeRates),
              }
            ], [])
          ],[]),
        monteCarlo: monteCarlo.monteCarlo5pctUV ? monteCarlo.monteCarlo5pctUV.map((_, i) => {
          const ret = { 
            type_tag: version, 
            date: timeFormat("%Y-%m-%dT%H:%M:%S")(timeYear.offset(new Date(monteCarlo.date), i)),
            dateDelta: timeFormat("%Y-%m-%dT%H:%M:%S")(timeYear.offset(new Date(0, 1, 1), i - 1900)) //increment by i years
          };
          monteCarloKeys.forEach(key => {
            if (Array.isArray(monteCarlo[key])) {
              ret[key] = monteCarlo[key][i];
            }
          });
          return ret;
        }) : null,
        aggregateFundamentals: portfolio.aggregateFundamentals[0].keys.reduce((t,k,i) => ({
          ...t,
          [k]: portfolio.aggregateFundamentals[0].values[i]
        }),{}),
        benchmarksAggregateFundamentals: benchmarks.map(b =>
          b.aggregateFundamentals[b.aggregateFundamentals.length - 1].keys.reduce((t,k,i) => ({
            ...t,
            [k]: b.aggregateFundamentals[b.aggregateFundamentals.length - 1].values[i]
          }),{ name: b.name })
        ),
        calendarYearPerformance: portfolio.calYearPerformance.keys.reduce((t,k,i) => ({
          ...t,
          [k]: portfolio.calYearPerformance.values[i]
        }),{}),
        benchmarksCalendarYearPerformance: benchmarks.map(b => 
          b.calYearPerformance.keys.reduce((t,k,i) => ({
            ...t,
            [k]: b.calYearPerformance.values[i]
          }), { name: b.name })
        )
      };
    });

    const stressTestsSelected = stressTestKeys.map(k => ({
      test: k,
      values: [
        { portfolio: "before", value: original.stressTests[k] },
        { portfolio: "after", value: proposed.stressTests[k] },
        ...(Object.keys(proposed.benchmarkStressTests).map(t => ({
          portfolio: t, value: proposed.benchmarkStressTests[t][k]
        })))
      ]
    }));

    const customStressTestsData = customStressTests.map(t => ({
      test: t.name,
      values: [
        { portfolio: "before", value: computeCustomStressTest(original.fm16FactorModel, t.factorShifts, riskFreeRates) },
        { portfolio: "after", value: computeCustomStressTest(proposed.fm16FactorModel, t.factorShifts, riskFreeRates) },
        ...(Object.keys(proposed.benchmarkFm16FactorModel).map(b => ({
          portfolio: b, value: computeCustomStressTest(proposed.benchmarkFm16FactorModel[b], t.factorShifts, riskFreeRates)
        })))
      ]
    }))

    const aggregateAggregateDistributions = [
      original.aggregateDistributions,
      proposed.aggregateDistributions
    ].map(
      (ad, i, a) =>
        ad.filter(o => o.calendarYear !== "N/A").length
          ? {
              type_tag: ad[0].type_tag,
              calendarYearLabel: ad[0].calendarYear !==
                ad[ad.length - 1].calendarYear
                ? ad[0].calendarYear +
                    "-" +
                    ad[ad.length - 1].calendarYear +
                    " Total"
                : ad[0].calendarYear + " Total",
              calendarYear: ad[0].calendarYear,
              ordinaryIncome: ad.reduce((t, v) => t + v.ordinaryIncome, 0)
            }
          : {
              type_tag: ad[0].type_tag,
              calendarYearLabel: "N/A",
              calendarYear: a[i + 1 % 2] &&
                a[i + 1 % 2].filter(o => o.calendarYear !== "N/A").length
                ? a[i + 1 % 2][0].calendarYear
                : new Date().getFullYear().toString(),
              ordinaryIncome: ad.reduce((t, v) => t + v.ordinaryIncome, 0)
            }
    );

    const originalByTicker = {};
    original.holdings.forEach(d => (originalByTicker[d.ticker] = d));
    const proposedByTicker = {};
    proposed.holdings.forEach(d => (proposedByTicker[d.ticker] = d));
    const parallelProposed = original.holdings.map(
      d => proposedByTicker[d.ticker]
    );
    parallelProposed.push(
      ...proposed.holdings.filter(
        d => !originalByTicker[d.ticker] && d.tickerWeighting
      )
    );

    const originalNotInProposed = original.holdings.filter(
      d => proposed.holdings.map(o => o.ticker).indexOf(d.ticker) === -1
    );
    const proposedNotInOriginal = proposed.holdings.filter(
      d => original.holdings.map(o => o.ticker).indexOf(d.ticker) === -1
    );

    return {
      raw: data,

      date: { original: original.date, proposed: proposed.date },
      versions: { original: original.version, proposed: proposed.version },
      holdings: { original: original.holdings, proposed: proposed.holdings },
      alignedHoldings: tickerPair(
        [
          [
            ...original.holdings.map(
              o => o && { ...o, weighting: o.tickerWeighting }
            )
          ],
          [
            ...parallelProposed.map(
              o => o && { ...o, weighting: o.tickerWeighting }
            )
          ]
        ],
        "ticker"
      ),
      levels: [...original.levels, ...proposed.levels],
      drawdownLevels: [...original.drawdownLevels, ...proposed.drawdownLevels],
      benchmarksLevels: original.benchmarksLevels,
      benchmarksDrawdownLevels: original.benchmarksDrawdownLevels,
      monteCarlo: original.monteCarlo && proposed.monteCarlo ? [...original.monteCarlo, ...proposed.monteCarlo] : null,
      aggregateDistributions: [
        ...original.aggregateDistributions.map(o => ({
          ...o,
          calendarYear: o.calendarYear === "N/A"
            ? (new Date()).getFullYear().toString()
            : o.calendarYear
        })),
        ...proposed.aggregateDistributions.map(o => ({
          ...o,
          calendarYear: o.calendarYear === "N/A"
            ? (new Date()).getFullYear().toString()
            : o.calendarYear
        }))
      ],
      aggregateAggregateDistributions,

      exposures: {
        assetClass: pair(
          [original.exposures.assetClass, proposed.exposures.assetClass],
          "value"
        ),
        marketCap: pair(
          [original.exposures.marketCap, proposed.exposures.marketCap],
          "value"
        ),
        sector: pair(
          [original.exposures.sector, proposed.exposures.sector],
          "value"
        ),
        country: pair(
          [original.exposures.country, proposed.exposures.country],
          "value"
        ),
        creditQuality: pair(
          [original.exposures.creditQuality, proposed.exposures.creditQuality],
          "value"
        ),
        maturity: pair(
          [original.exposures.maturity, proposed.exposures.maturity],
          "value"
        ),
        // currency: pair(
        //   [original.exposures.currency, proposed.exposures.currency],
        //   "value"
        // )
      },
      benchmarksExposures: original.benchmarksExposures,
      stressTests: stressTestsSelected,
      famaFrench5Factors: [
        { ...original.famaFrench5Factors, ticker: "original" },
        { ...proposed.famaFrench5Factors, ticker: "proposed" }
      ],
      performance: Object.keys(performanceTitles).map(key => [
        { ...original.performance[key], ticker: "original" },
        { ...proposed.performance[key], ticker: "proposed" },
        ...original.benchmarksPerformance.map(b => ({ ...b[key], ticker: b[key].name }) )
      ]),

      uniques: {
        original: originalNotInProposed,
        proposed: proposedNotInOriginal
      },

      tickersUSA: {
        original: original.holdings
          .filter(p => p.geoFocus === "United States of America" 
            && p.assetClass === "Equity"
            && famaFrench5FactorsKeys.reduce((t, v) => 
              t + Math.abs(p["fff-" + v]),0) > 0 ),
        proposed: proposed.holdings
          .filter(p => p.geoFocus === "United States of America" 
            && p.assetClass === "Equity"
            && famaFrench5FactorsKeys.reduce((t, v) => 
              t + Math.abs(p["fff-" + v]),0) > 0 ),
      },

      fm16FactorModel: { original: original.fm16FactorModel, proposed: proposed.fm16FactorModel },
      customStressTestsData: { 
        asset: customStressTests.map((d,i) => ({
          original: original.customStressTestsData[i].map(d => ({ ...d, exit: !proposedByTicker[d.key] })),
          proposed: proposed.customStressTestsData[i],
        })),
        portfolio: customStressTestsData 
      },
      aggregateFundamentals: Object.keys(original.aggregateFundamentals)
        .map((k,i) => ({
          key: k,
          original: original.aggregateFundamentals[k],
          proposed: proposed.aggregateFundamentals[k],
          benchmarks: original.benchmarksAggregateFundamentals.map(b => b[k]),
          benchmarksNames: original.benchmarksAggregateFundamentals.map(b => b.name)
        })),
      calendarYearPerformance: Array.from([
          ...Object.keys(original.calendarYearPerformance),
          ...Object.keys(proposed.calendarYearPerformance),
        ])
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
        .map(k => ({
          key: k,
          original: original.calendarYearPerformance[k],
          proposed: proposed.calendarYearPerformance[k],
          benchmarks: original.benchmarksCalendarYearPerformance.map(b => b[k]),
          benchmarksNames: original.benchmarksCalendarYearPerformance.map(b => b.name)
        }))
    };
  }
);



// WEBPACK FOOTER //
// ./src/Report/selectors.js